import type { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Chip,
  CircularProgress,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { useTranslation } from 'react-i18next';
import ScrollableTableContainer from '@components/common/ScrollableTableContainer';
import type { GroupedOrgFinding } from '@services/findingsService';
import { severityChipColor, SEVERITY_FALLBACK } from '@utils/findingSeverity';
import { stripCodeSpans } from '@utils/codeSpans';
import { RuleTitle } from '@components/findings/RuleTitle';

/**
 * REV-1097: the findings list with one row per issue rather than per page.
 *
 * A defect in a shared header is one FindingInstance per page that carries the
 * header, so the per-page list repeats it for every page. Here it is one row,
 * with how many pages and findings it covers, and a link to those findings.
 * The rows are the API's groups (revenant-api src/utils/findingGroups.js):
 * the worst severity and the least settled status of the group.
 *
 * Its own component rather than a mode of FindingsTable: a group has no single
 * page, cannot be selected for a per-finding bulk action, and is not a finding
 * the detail page can open. Each of those would be a prop FindingsTable has to
 * turn off.
 */
export interface GroupedFindingsTableProps {
  rows: GroupedOrgFinding[];
  loading?: boolean;
  empty?: ReactNode;
  /** Where a group's own findings are listed. */
  hrefFor: (g: GroupedOrgFinding) => string;
  /** id of the heading that names this table. Prefer this over `ariaLabel`. */
  labelledBy?: string;
  ariaLabel?: string;
  /** Name for the scrollable region around the table (REV-507). */
  regionLabel?: string;
  footer?: ReactNode;
  outlined?: boolean;
}

const COLUMN_COUNT = 6;

export default function GroupedFindingsTable({
  rows,
  loading = false,
  empty,
  hrefFor,
  labelledBy,
  ariaLabel,
  regionLabel,
  footer,
  outlined = false,
}: GroupedFindingsTableProps) {
  const { t } = useTranslation();

  const statusLabel = (g: GroupedOrgFinding) =>
    g.source === 'manual'
      ? t('findings.grouped.failedInAudit', 'Failed in an audit')
      : t(`findings.statuses.${g.status}`, g.status);

  return (
    <ScrollableTableContainer
      {...(regionLabel ? { label: regionLabel } : {})}
      {...(outlined ? { component: Paper, variant: 'outlined' as const } : {})}
    >
      <Table
        {...(labelledBy ? { 'aria-labelledby': labelledBy } : {})}
        {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
      >
        <TableHead>
          <TableRow>
            <TableCell>{t('findings.columns.rule', 'Rule')}</TableCell>
            <TableCell>{t('findings.columns.severity', 'Severity')}</TableCell>
            <TableCell>{t('findings.grouped.status', 'Status')}</TableCell>
            <TableCell>{t('findings.columns.context', 'Tracked place')}</TableCell>
            <TableCell align="right">{t('findings.grouped.pages', 'Pages')}</TableCell>
            <TableCell align="right">{t('findings.grouped.findings', 'Findings')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading && (
            <TableRow>
              <TableCell colSpan={COLUMN_COUNT} align="center">
                <CircularProgress size={24} />
              </TableCell>
            </TableRow>
          )}
          {!loading && rows.length === 0 && empty && (
            <TableRow>
              <TableCell colSpan={COLUMN_COUNT} align="center">
                <Typography color="text.secondary">{empty}</Typography>
              </TableCell>
            </TableRow>
          )}
          {!loading &&
            rows.map((g) => {
              const rule = stripCodeSpans(g.rule_name || g.rule_id || '');
              return (
                <TableRow key={g.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      <RuleTitle text={g.rule_name || g.rule_id || '—'} />
                    </Typography>
                    {g.element_selector && (
                      <Typography
                        component="code"
                        variant="body2"
                        color="text.secondary"
                        sx={{ display: 'block', wordBreak: 'break-all', mt: 0.5 }}
                      >
                        {g.element_selector}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={severityChipColor(g.severity)}
                      label={t(`findings.severities.${g.severity}`, SEVERITY_FALLBACK[g.severity])}
                    />
                  </TableCell>
                  <TableCell>{statusLabel(g)}</TableCell>
                  <TableCell>{g.context_name || '—'}</TableCell>
                  <TableCell align="right">
                    {g.source === 'manual'
                      ? '—'
                      : t('findings.grouped.pageCount', {
                          count: g.page_count,
                          defaultValue: '{{count}} pages',
                        })}
                  </TableCell>
                  <TableCell align="right">
                    {/* A link, not a button: it changes the URL. The visible
                        text is the count; the hidden part says which group,
                        because every row's link would otherwise read the same
                        out of context ("2 findings"). */}
                    <Link component={RouterLink} to={hrefFor(g)} underline="hover">
                      {t('findings.grouped.findingCount', {
                        count: g.instance_count,
                        defaultValue: '{{count}} findings',
                      })}
                      <Box component="span" sx={visuallyHidden}>
                        {g.element_selector
                          ? t('findings.grouped.linkContextElement', {
                              defaultValue: ' for {{rule}} on {{element}}',
                              rule,
                              element: g.element_selector,
                            })
                          : t('findings.grouped.linkContext', {
                              defaultValue: ' for {{rule}}',
                              rule,
                            })}
                      </Box>
                    </Link>
                  </TableCell>
                </TableRow>
              );
            })}
        </TableBody>
      </Table>
      {footer}
    </ScrollableTableContainer>
  );
}
